import {
    AdvancedMarker,
    AdvancedMarkerAnchorPoint,
    Map,
    StaticMap,
    createStaticMapsUrl,
} from '@vis.gl/react-google-maps';
import React from 'react';

type Props = {
    lat: number;
    long: number;
    label?: string;
};

/**
 * Renders a dynamic Google Map component centered at a given location with a marker.
 * It provides interactive features and can display an optional custom label next to the marker.
 *
 * @param {object} props - The properties for the component.
 * @param {number} props.lat - The latitude for the map's center and marker position.
 * @param {number} props.long - The longitude for the map's center and marker position.
 * @param {string} [props.label] - An optional text label to display adjacent to the marker.
 * @returns {JSX.Element} A React component rendering an interactive Google Map.
 */
function GoogleMapDynamic({ lat, long, label }: Props) {
    const position = { lat, lng: long };

    return (
        <Map defaultCenter={position} defaultZoom={16} disableDefaultUI={true} mapId="63a42379136971b6">
            <AdvancedMarker position={position} />
            {label && (
                <AdvancedMarker position={position} anchorPoint={AdvancedMarkerAnchorPoint.LEFT_BOTTOM}>
                    <div className="left-full py-2 pl-4 text-left font-sans text-base font-semibold text-gray-600 shadow-white [text-shadow:_0_1px_0_var(--tw-shadow-color)]">
                        {label}
                    </div>
                </AdvancedMarker>
            )}
        </Map>
    );
}

/**
 * Renders a static Google Map Image centered at a given location with a marker.
 *
 * @param {object} props - The properties for the component.
 * @param {number} props.lat - The latitude for the map's center and marker position.
 * @param {number} props.long - The longitude for the map's center and marker position.
 * @param {string} [props.label] - An optional text label to display adjacent to the marker.
 * @returns {JSX.Element} A React component rendering a static Image from Google Maps.
 */
function GoogleMapStatic({ lat, long, label }: Props) {
    let markerLabel = '';
    if (label && label.length > 0) {
        // Google Static Maps API `label` property is limited to a single
        // alphanumeric character (A-Z, 0-9).
        const firstAlphanumericMatch = label.match(/[a-zA-Z0-9]/);

        if (firstAlphanumericMatch && firstAlphanumericMatch[0]) {
            markerLabel = firstAlphanumericMatch[0];
        }
    }

    const staticMapsUrl = createStaticMapsUrl({
        apiKey: import.meta.env.VITE_GOOGLE_API_STATIC_MAPS_KEY,
        scale: 2,
        zoom: 16,
        width: 378,
        height: 210,
        center: { lat: lat, lng: long },
        format: 'png',
        markers: [
            {
                location: { lat: lat, lng: long },
                color: 'red',
                label: markerLabel,
            },
        ],
        style: [
            {
                featureType: 'poi',
                stylers: [{ visibility: 'off' }],
            },
        ],
    });

    return (
        <div>
            <StaticMap className="map" url={staticMapsUrl} />
        </div>
    );
}

export { GoogleMapDynamic, GoogleMapStatic };
